import AccessLevels from "Areas/SharedFeatures/accessLevels";
import AccessLevelsCreatorPanel from "Areas/SharedFeatures/accessLevelsCreatorPanel";
import AccessLevelsEditorPanel from "Areas/SharedFeatures/accessLevelsEditorPanel";
import TaskStatistics from "Areas/SharedFeatures/taskStatistics";
import TasksTable from "Areas/SharedFeatures/tasksTable";
import { WITH_APP_PAGE_BODY_SIZES_CLASS_NAME } from "Common/componentUtils/constants";
import { ACCESS_LEVEL_ENTITY } from "Common/dictionaries/accessLevelEntity";
import Menu from "Components/menu";
import ProgressOverlay from "Components/progressOverlay";
import { Tab, TabPanel, Tabs } from "Components/tabs";
import classNames from "classnames";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import React, { useCallback, useEffect, useMemo } from "react";
import { useResizeDetector } from "react-resize-detector";
import DocumentList from "./documentList";
import locale from "./projectDetails.locale";
import SummaryReportList from "./summaryReportList";
import Button from "Components/button";
import styles from "./projectDetails.module.css";
export default function ProjectDetails(props) {
    var _a, _b, _c, _d;
    const { height, ref: tabContentObservableRef } = useResizeDetector();
    const { width, ref: rightPanelObservableRef } = useResizeDetector();
    useEffect(() => {
        if (props.projectId && props.onInitialized) {
            props.onInitialized(props.projectId);
        }
    }, [props.projectId]);
    const tabs = useMemo(() => compact([
        props.canViewDocuments && {
            id: 1,
            title: locale.DOCUMENTS,
            render: () => { var _a; return React.createElement(DocumentList, { data: (_a = props.data) === null || _a === void 0 ? void 0 : _a.Documents }); }
        },
        props.canViewSummaryReports && {
            id: 4,
            title: locale.SUMMARY_REPORTS,
            render: () => React.createElement(SummaryReportList, { projectId: props.projectId })
        },
        props.canViewTasks && {
            id: 2,
            title: locale.TASKS,
            render: () => React.createElement(TasksTable, { projectId: props.projectId })
        },
        props.canViewTasks && {
            id: 3,
            title: locale.TASK_STATISTICS,
            render: () => React.createElement(TaskStatistics, { projectId: props.projectId })
        }
    ]), [
        props.data,
        props.projectId,
        props.canViewDocuments,
        props.canViewTasks,
        props.canViewSummaryReports
    ]);
    const menuItems = useMemo(() => compact([
        props.canEditProject && {
            text: locale.MANAGE_ACCESS,
            onClick: () => {
                props.onOpenAccessLevelsEditorPanel &&
                    props.projectId &&
                    props.onOpenAccessLevelsEditorPanel(props.projectId);
            }
        },
        props.canEditProject && {
            divider: true
        },
        props.canEditProject && {
            text: locale.EDIT_PROJECT,
            onClick: () => {
                props.onOpenProjectEditorPanel &&
                    props.data &&
                    props.onOpenProjectEditorPanel(props.data.Id);
            }
        },
        props.canDeleteProject && {
            text: locale.DELETE_PROJECT,
            confirm: {
                title: locale.CONFIRM_DELETE_PROJECT_TEXT,
                header: locale.CONFIRM_DELETE_PROJECT_HEADER,
                callback: () => {
                    var _a;
                    ((_a = props.data) === null || _a === void 0 ? void 0 : _a.Id) &&
                        props.onDeleteProject &&
                        props.onDeleteProject(props.data.Id, props.data.WorkSpaceId);
                }
            }
        }
    ]), [props.data, props.canEditProject, props.canDeleteProject]);
    const tabData = useMemo(() => {
        var _a;
        return ((_a = props.data) === null || _a === void 0 ? void 0 : _a.Documents)
            ? locale.buildCountDocumentsTitle(props.data.Documents.length)
            : locale.buildCountDocumentsTitle(0);
    }, [props.data, props.projectId, props.canViewDocuments, props.tab]);
    const handleCreateDocument = useCallback(() => {
        props.projectId &&
            props.onOpenDocumentCreatorPanel &&
            props.onOpenDocumentCreatorPanel(props.projectId);
    }, [props.projectId, props.onOpenDocumentCreatorPanel]);
    return (React.createElement(ProgressOverlay, { show: props.inProgress }, props.data ? (React.createElement("div", { className: classNames("grid-rows-auto-1fr", WITH_APP_PAGE_BODY_SIZES_CLASS_NAME) },
        React.createElement("div", null,
            React.createElement("div", { className: classNames("grid-cols-1fr-auto", styles.header) },
                React.createElement("div", { className: "overflow-hidden" },
                    React.createElement("div", { className: styles.title },
                        React.createElement("h2", { title: (_a = props.data) === null || _a === void 0 ? void 0 : _a.Name, className: classNames("text-overflow-ellipsis nowrap", styles.name) }, ((_b = props.data) === null || _b === void 0 ? void 0 : _b.Name) || ""),
                        tabData && (React.createElement("div", { className: classNames("w-fit-content", styles["tab-data"]) }, tabData))),
                    React.createElement("div", { title: (_c = props.data) === null || _c === void 0 ? void 0 : _c.Description, className: "text-muted text-overflow-ellipsis nowrap" }, ((_d = props.data) === null || _d === void 0 ? void 0 : _d.Description) || "")),
                React.createElement("div", { className: styles.actions },
                    props.canEditDocuments && (React.createElement(Button, { text: locale.CREATE_DOCUMENT, onClick: handleCreateDocument, className: "btn-primary btn-with-icon", icon: "add", height: "36px" })),
                    !isEmpty(menuItems) && (React.createElement(Menu, { togglerClassName: "menu-toggle-btn-border", togglerIconClassName: "icon-dots menu-toggle-btn-border-icon", items: menuItems, togglerHeight: "36px", togglerWidth: "36px" }))))),
        tabs.length > 0 && props.projectId && (React.createElement("div", { className: "d-flex justify-content-space-between panel-body-pb overflow-hidden", ref: tabContentObservableRef },
            React.createElement(Tabs, { className: classNames("w-100 with-gap-16", styles.tabs), onTabChange: props.onChangeTab, selectedTabIndex: props.tab, rightPanelWidth: width ? width + 15 : 0, flex: true, useGap: false }, map(tabs, item => {
                var _a;
                return [
                    React.createElement(Tab, { title: item.title, key: `tab-${item.id}`, rightPanel: props.canEditProject ? (React.createElement("div", { ref: rightPanelObservableRef },
                            React.createElement(AccessLevels, { openedHeight: height, defaultOpen: props.tab === 0, entityId: props.projectId, entityType: ACCESS_LEVEL_ENTITY.PROJECT, userCreate: (_a = props.data) === null || _a === void 0 ? void 0 : _a.UserCreated }))) : undefined }),
                    React.createElement(TabPanel, { key: `tab-panel-${item.id}`, className: classNames(tabs.length > 0 && props.projectId ? "ml-gap mr-gap" : "panel-body-padding") }, item.render ? item.render() : React.createElement("div", null))
                ];
            })),
            props.canEditProject && (React.createElement(AccessLevelsEditorPanel, { entityId: props.projectId, entityType: ACCESS_LEVEL_ENTITY.PROJECT, userCreate: props.data.UserCreated })),
            props.canEditProject && (React.createElement(AccessLevelsCreatorPanel, { entityId: props.projectId, entityType: ACCESS_LEVEL_ENTITY.PROJECT, userCreate: props.data.UserCreated })))))) : (React.createElement("div", null))));
}
